import { TaggingSteps } from 'enums/TaggingSteps';
import { ITaggingFlowOption } from 'interfaces/tagging/ITaggingFlowOption';
import { CallOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/CallOptions';
import { PitchOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/PitchOptions';
import { Play } from 'models/tagging/Play';
import { AutomaticOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/AutomaticOptions';
import { HitOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/HitOptions';
import { ResultOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/ResultOptions';
import { OutsOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/OutsOptions';
import { RunsOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/RunsOptions';
import { ITagCatcherThrows } from 'interfaces/tagging/ITag';
import { CallTypes, HitTypes, PitchTypes, ResultTypes, IApiPlayerExtended } from '@trackman/baseball-shared-components';
import { BaseRunners } from 'models/baseRunners/BaseRunners';
import { InPlayResultOptions } from '../../pages/Tagging/components/TaggingFlow/optionArrays/InPlayResultOptions';

export type optionValueType = string | number | ITagCatcherThrows | IApiPlayerExtended[] | BaseRunners;
export const resumeInterrupted = 'ResumeInterrupted';
interface ITaggingFlowStep {
  name: TaggingSteps;
  options: ITaggingFlowOption[];
  automaticOptions?: ITaggingFlowOption[];
  hasCatcherThrows: boolean;
  hasBaseRunners: boolean;
  hasFieldingSequence: boolean;
  breadcrumbTitle: string;
  getNextStep: (value: optionValueType, isPlayerTrackingEnabled?: boolean) => TaggingSteps | 'ResumeInterrupted';
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => void;
  clearUpdate: (play: Play) => void;
}

const pitchStep: ITaggingFlowStep = {
  name: TaggingSteps.Pitch,
  options: PitchOptions,
  breadcrumbTitle: 'What was the pitch type?',
  hasCatcherThrows: false,
  hasBaseRunners: false,
  hasFieldingSequence: false,
  getNextStep: (value: optionValueType) => TaggingSteps.Call,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => (play.tag.pitchType = value as string),
  clearUpdate: (play: Play) => (play.tag.pitchType = PitchTypes.Undefined),
};

const callStep: ITaggingFlowStep = {
  name: TaggingSteps.Call,
  options: CallOptions,
  automaticOptions: AutomaticOptions,
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  breadcrumbTitle: 'What was the call?',
  getNextStep: (value: optionValueType, isPlayerTrackingEnabled?: boolean) =>
    value === CallTypes.InPlay ? TaggingSteps.Hit : isPlayerTrackingEnabled ? TaggingSteps.Result : TaggingSteps.Outs,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => {
    play.tag.pitchCall = value as string;
    play.tag.pitchCallDisplay = displayValue;
  },
  clearUpdate: (play: Play) => {
    play.tag.pitchCall = CallTypes.Undefined;
    play.tag.pitchCallDisplay = CallTypes.Undefined;
  },
};

const hitStep: ITaggingFlowStep = {
  name: TaggingSteps.Hit,
  options: HitOptions,
  breadcrumbTitle: 'What was the hit type?',
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  getNextStep: (value: optionValueType) => TaggingSteps.InPlayResult,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => (play.tag.hitType = value as string),
  clearUpdate: (play: Play) => (play.tag.hitType = HitTypes.Undefined),
};

const resultStep: ITaggingFlowStep = {
  name: TaggingSteps.Result,
  options: ResultOptions,
  breadcrumbTitle: 'What happened on the basepaths?',
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  getNextStep: (value: optionValueType) => TaggingSteps.Outs,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => (play.tag.playResult = value as string),
  clearUpdate: (play: Play) => (play.tag.playResult = ResultTypes.Undefined),
};

const inPlayResultStep: ITaggingFlowStep = {
  name: TaggingSteps.InPlayResult,
  options: InPlayResultOptions,
  breadcrumbTitle: 'What was the result?',
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  getNextStep: (value: optionValueType) => TaggingSteps.Outs,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => (play.tag.playResult = value as string),
  clearUpdate: (play: Play) => (play.tag.playResult = ResultTypes.Undefined),
};

const outStep: ITaggingFlowStep = {
  name: TaggingSteps.Outs,
  options: OutsOptions,
  breadcrumbTitle: 'How many outs in this play?',
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  getNextStep: (value: optionValueType) => TaggingSteps.Runs,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => play.setOuts(value as number),
  clearUpdate: (play: Play) => play.setOuts(0),
};

const runStep: ITaggingFlowStep = {
  name: TaggingSteps.Runs,
  options: RunsOptions,
  breadcrumbTitle: 'How many runs in this play?',
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  getNextStep: (value: optionValueType) => TaggingSteps.Final,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => play.setRuns(value as number),
  clearUpdate: (play: Play) => play.setRuns(0),
};

const finalStep: ITaggingFlowStep = {
  name: TaggingSteps.Final,
  options: RunsOptions,
  breadcrumbTitle: 'Finished',
  hasCatcherThrows: true,
  hasBaseRunners: true,
  hasFieldingSequence: true,
  getNextStep: (value: optionValueType) => TaggingSteps.Final,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearUpdate: (play: Play) => {},
};

const catcherThrowsStep: ITaggingFlowStep = {
  name: TaggingSteps.CatcherThrows,
  options: [],
  breadcrumbTitle: 'Who did the catcher throw to?',
  hasCatcherThrows: false,
  hasBaseRunners: false,
  hasFieldingSequence: false,
  getNextStep: (value: optionValueType) => resumeInterrupted,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => (play.tag.catcherThrows = value as ITagCatcherThrows),
  clearUpdate: (play: Play) => (play.tag.catcherThrows = undefined),
};

const baseRunnersStep: ITaggingFlowStep = {
  name: TaggingSteps.BaseRunners,
  options: [],
  breadcrumbTitle: 'Where are runners positioned?',
  hasCatcherThrows: false,
  hasBaseRunners: false,
  hasFieldingSequence: false,
  getNextStep: () => resumeInterrupted,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => {
    play.baseRunners = value as BaseRunners;
    play.isBRChangedManually = true;
    play.isChanged = true;
  },
  clearUpdate: (play: Play) => {
    play.baseRunners = { firstBase: null, secondBase: null, thirdBase: null };
  },
};

const fieldingSequenceStep: ITaggingFlowStep = {
  name: TaggingSteps.FieldingSequence,
  options: [],
  breadcrumbTitle: 'Who fielded the ball?',
  hasCatcherThrows: false,
  hasBaseRunners: false,
  hasFieldingSequence: false,
  getNextStep: () => resumeInterrupted,
  updatePlay: (play: Play, value: optionValueType, displayValue: string) => {
    play.fieldingSequence = value as (IApiPlayerExtended | null)[];
    play.isChanged = true;
    play.isFieldingSequenceChanged = true;
  },
  clearUpdate: (play: Play) => {
    play.fieldingSequence = [];
  },
};

export const steps = {
  [TaggingSteps.Pitch]: pitchStep,
  [TaggingSteps.Call]: callStep,
  [TaggingSteps.Hit]: hitStep,
  [TaggingSteps.Result]: resultStep,
  [TaggingSteps.InPlayResult]: inPlayResultStep,
  [TaggingSteps.Outs]: outStep,
  [TaggingSteps.Runs]: runStep,
  [TaggingSteps.Final]: finalStep,
  [TaggingSteps.CatcherThrows]: catcherThrowsStep,
  [TaggingSteps.BaseRunners]: baseRunnersStep,
  [TaggingSteps.FieldingSequence]: fieldingSequenceStep,
};
